import React, { useEffect, useState } from "react";
import { api, API, coverUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminBundles() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/bundles").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/admin/products").then((r) => setProducts(r.data.filter(p => p.status === "live"))); }, []);

  const save = async () => {
    try {
      const payload = { title: edit.title, description: edit.description || "",
        price: parseFloat(edit.price), product_ids: edit.product_ids || [],
        status: edit.status || "live", featured: !!edit.featured };
      if (edit.id) await api.patch(`/admin/bundles/${edit.id}`, payload);
      else await api.post("/admin/bundles", payload);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error("Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete bundle?")) return; await api.delete(`/admin/bundles/${id}`); load(); };
  const toggle = (pid) => setEdit({ ...edit, product_ids: (edit.product_ids || []).includes(pid) ? edit.product_ids.filter(x => x !== pid) : [...(edit.product_ids || []), pid] });

  return (
    <div data-testid="admin-bundles">
      <div className="flex items-end justify-between border-b border-border pb-4 mb-6">
        <div><h1 className="font-display text-4xl tracking-tight">Bundles</h1>
          <p className="text-sm text-muted-foreground italic mt-1">Group titles together at a special price.</p></div>
        <Button onClick={() => setEdit({ title: "", description: "", price: 19.99, product_ids: [], status: "live", featured: false })} className="rounded-sm" data-testid="new-bundle"><Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />New bundle</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <div className="col-span-3 text-center py-10 text-sm text-muted-foreground italic border border-dashed border-border rounded-sm">No bundles yet.</div>}
        {items.map((b) => (
          <div key={b.id} className="border border-border rounded-sm p-4 bg-card" data-testid={`bundle-${b.id}`}>
            <div className="font-display text-xl">{b.title}</div>
            <div className="text-xs text-muted-foreground italic mt-1 line-clamp-2">{b.description}</div>
            <div className="text-2xl font-semibold mt-3">A${b.price?.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground mt-1">{b.product_ids?.length || 0} titles · {b.status}</div>
            <div className="flex gap-1 mt-3">
              <Button variant="ghost" size="sm" onClick={() => setEdit(b)}>Edit</Button>
              <Button variant="ghost" size="sm" onClick={() => remove(b.id)}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
            <DialogHeader><DialogTitle className="font-display text-2xl">{edit.id ? "Edit bundle" : "New bundle"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="rounded-sm mt-1.5" data-testid="bu-title" /></div>
              <div><Label>Description</Label><Textarea rows={3} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="rounded-sm mt-1.5" /></div>
              <div><Label>Price (AUD)</Label><Input type="number" step="0.01" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} className="rounded-sm mt-1.5 max-w-32" data-testid="bu-price" /></div>
              <div>
                <Label>Titles in this bundle</Label>
                <div className="mt-2 max-h-64 overflow-y-auto border border-border rounded-sm divide-y divide-border">
                  {products.map((p) => (
                    <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40 cursor-pointer">
                      <input type="checkbox" checked={(edit.product_ids || []).includes(p.id)} onChange={() => toggle(p.id)} />
                      <img src={coverUrl(p)} alt="" className="w-7 h-9 object-cover rounded-sm border border-border/60" />
                      <span className="text-sm flex-1">{p.title}</span>
                      <span className="text-xs text-muted-foreground">A${p.price.toFixed(2)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={!!edit.featured} onCheckedChange={(v) => setEdit({ ...edit, featured: v })} id="bu-feat" /><Label htmlFor="bu-feat">Feature on home</Label></div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} data-testid="bu-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
